import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AnimeCard from '@/components/feature/AnimeCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { animeList } from '@/mocks/anime';

export default function AnimeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === id);

  if (!anime) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-800 mb-2">动漫不存在</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedAnime = animeList
    .filter((a) => a.id !== anime.id && (a.categorySlug === anime.categorySlug || a.tags.some((t) => anime.tags.includes(t))))
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${anime.title} - 羞羞动漫网站`,
    description: anime.description,
    datePublished: anime.year.toString(),
    dateModified: anime.updateDate,
    author: {
      '@type': 'Organization',
      name: '羞羞动漫网站',
    },
    publisher: {
      '@type': 'Organization',
      name: '羞羞动漫网站',
      url: 'https://www.xiuxiudongman.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.xiuxiudongman.com/anime/${anime.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: anime.category, href: `/category/${anime.categorySlug}` },
                { label: anime.title },
              ]}
            />

            <div className="mt-6 flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="lg:w-72 shrink-0">
                <div className="rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={anime.coverImage}
                    alt={`${anime.title} - 羞羞动漫在线蜜桃`}
                    title={`${anime.title} | 动漫美女角色 | 羞羞动漫`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
                <div className="mt-4 bg-white rounded-lg border border-background-200/70 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">评分</span>
                    <span className="text-sm font-medium text-accent-500 whitespace-nowrap">{anime.rating}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">集数</span>
                    <span className="text-sm text-foreground-800 whitespace-nowrap">{anime.episodes}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">状态</span>
                    <span className={`text-sm whitespace-nowrap ${anime.status === '连载中' ? 'text-accent-500' : 'text-foreground-800'}`}>
                      {anime.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">年份</span>
                    <span className="text-sm text-foreground-800 whitespace-nowrap">{anime.year}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">制作</span>
                    <span className="text-sm text-foreground-800 whitespace-nowrap">{anime.studio}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">观看</span>
                    <span className="text-sm text-foreground-800 whitespace-nowrap">{anime.views.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {anime.title}
                </h1>

                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <Link
                    to={`/category/${anime.categorySlug}`}
                    className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium whitespace-nowrap"
                  >
                    {anime.category}
                  </Link>
                  {anime.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-file-text-line text-primary-500"></i>
                    </div>
                    <h2 className="text-base font-medium text-foreground-900">动漫简介</h2>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{anime.description}</p>
                </div>

                {anime.characters.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-user-heart-line text-primary-500"></i>
                      </div>
                      <h2 className="text-base font-medium text-foreground-900">动漫美女角色</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {anime.characters.map((char, i) => (
                        <div key={i} className="flex gap-3 p-3 bg-white rounded-lg border border-background-200/70">
                          <img
                            src={char.imageUrl}
                            alt={`${char.name} - ${char.role}`}
                            title={`${char.name} | 动漫美女角色`}
                            className="w-16 h-16 rounded-lg object-cover object-top shrink-0"
                          />
                          <div className="min-w-0">
                            <h3 className="text-sm font-medium text-foreground-900 whitespace-nowrap">{char.name}</h3>
                            <p className="text-xs text-primary-500 mb-1 whitespace-nowrap">{char.role}</p>
                            <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{char.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-background-200/70">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-play-circle-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">立即观看</p>
                    <p className="text-xs text-foreground-500 whitespace-nowrap">
                      羞羞动漫网站 · 免费在线观看 · 51动漫国产精品入口
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {relatedAnime.length > 0 && (
              <section className="mt-10 md:mt-14">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-primary-500"></i>
                  </div>
                  <h2 className="text-lg font-heading text-foreground-900">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-product-shop="">
                  {relatedAnime.map((a) => (
                    <AnimeCard key={a.id} anime={a} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}